/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import memoize from 'lodash/memoize';
import { createExpirableStorageProvider } from '../../createExpirableStorageProvider.tsx';
import { createStorageProvider } from '../../createStorageProvider.tsx';
import {
	type ExpirableWebStorageProvider,
	type WebStorageProvider,
	WebStorageType,
} from '../../types.tsx';
import { makeSessionExpirableStore, makeSessionStore } from './utils.tsx';

/**
 * Creates a memoized sessionStorage-backed storage provider that persists only for the current browser tab/window.
 * Data is cleared when the tab/window closes. Not suitable for expirable data - use createSessionExpirableStorageProvider instead.
 *
 * @agent-skills: browser-storage, state-management
 */
export const createSessionStorageProvider: ((
	appPrefix?: string | undefined,
) => WebStorageProvider) &
	import('lodash').MemoizedFunction = memoize(
	(appPrefix?: string | undefined): WebStorageProvider =>
		createStorageProvider(WebStorageType.SessionStorage, appPrefix, makeSessionStore()),
);

/**
 * Creates a memoized sessionStorage-backed storage provider with automatic expiration support.
 * Data persists only for the current browser tab/window until explicitly expired. Not suitable for non-expirable data.
 *
 * @agent-skills: browser-storage, state-management
 */
export const createSessionExpirableStorageProvider: ((
	appPrefix: string,
) => ExpirableWebStorageProvider) &
	import('lodash').MemoizedFunction = memoize((appPrefix: string): ExpirableWebStorageProvider => {
	if (!appPrefix) {
		throw Error("appPrefix can not be empty. It's used to separate apps");
	}

	return createExpirableStorageProvider(
		WebStorageType.SessionStorage,
		appPrefix,
		makeSessionExpirableStore(),
	);
});
